import {Component, Input, OnInit, OnDestroy} from '@angular/core';
import {ConfirmationService, MenuItem, ToastMessageOptions, MessageService} from 'primeng/api';
import {
  CruiseControlSelectionCriteriaInterface
} from '../../../../../model/cruise-control-selection-criteria.interface';
import {CruiseControlSettingsInterface} from '../../../../../model/cruise-control-settings.interface';
import {CruiseControlStatusInterface} from '../../../../../model/cruise-control-status.interface';
import {CruiseControlService} from '../../../../../service/cruise-control.service';
import {UserService} from '../../../../../service/user.service';
import {repeat, Subject, takeUntil} from 'rxjs';

@Component({
  selector: 'auto-cruise-control-form-legacy',
  templateUrl: './cruise-control-form-legacy.component.html',
  styleUrl: './cruise-control-form-legacy.component.scss',
  standalone: false
})
export class CruiseControlFormLegacyComponent implements OnInit, OnDestroy {
  readonly cruiseControlService: CruiseControlService;
  readonly messageService: MessageService;
  readonly confirmationService: ConfirmationService;
  readonly userService: UserService;
  @Input() accountId: number;
  @Input() settings: CruiseControlSettingsInterface;
  @Input() label: string;
  generateAsOptions: object[] = [{value: false, label: 'Active'}, {value: true, label: 'Inactive'}]
  generateAllInventoryOptions: object[] = [{value: true, label: 'On'}, {value: false, label: 'Off'}]
  buildByOptions: object[] = [{value: 1, label: 'Model'}, {value: 2, label: 'Trim'}, {value: 3, label: 'Vehicle'}]
  selectionCriteria: CruiseControlSelectionCriteriaInterface[] = [];
  minimumImageOptions: object[] = []
  status: CruiseControlStatusInterface | null = null;
  progressSteps: MenuItem[] = [{label: 'Queued'}, {label: 'In Progress'}, {label: 'Done'}];
  progressIndex: number = 0;
  commandMessages: ToastMessageOptions[] = [];
  pollingSubject: Subject<any> = new Subject<any>();
  destroySubject: Subject<void> = new Subject<void>();
  lastCommandCreated: string;
  monitorNewCommand: boolean = false;
  offerSetVisibilityScopeEnabled: boolean = false;
  constructor(cruiseControlService: CruiseControlService, messageService: MessageService, confirmationService: ConfirmationService, userService: UserService) {
    this.cruiseControlService = cruiseControlService;
    this.messageService = messageService;
    this.confirmationService = confirmationService;
    this.userService = userService;
  }
  /**
   * Init component
   */
  ngOnInit(): void {
    this.populateSelectionCriteria();
    this.populateMinimumImageRequirementOptions();
    this.updateStatus();
    this.checkFeatureFlags();
  }
  /**
   * Cleanup component
   */
  ngOnDestroy(): void {
    this.cancelStatusPolling();
    this.pollingSubject.complete();
    this.destroySubject.next();
    this.destroySubject.complete();
  }
  /**
   * Checks feature flags for the account
   */
  checkFeatureFlags(): void {
    this.userService.getMpopFeature('offerSetVisibilityScope')
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (feature) => {
          if (feature && feature[0]) {
            this.offerSetVisibilityScopeEnabled = feature[0].enabled;
          }
        },
        error: () => this.offerSetVisibilityScopeEnabled = false
      });
  }
  /**
   * Populates options for the minimum image requirements form field
   */
  populateMinimumImageRequirementOptions(): void {
    for (let i = 0; i < 21; i++) {
      this.minimumImageOptions.push({ value: i, label: i });
    }
  }
  /**
   * Populates the vehicle selection criteria options for the Vehicle Selection Criteria form field
   */
  populateSelectionCriteria(): void {
    this.cruiseControlService.getSelectionCriteria()
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (list) => {
          for (const item of list) {
            if (this.settings.vehicleCondition === item.vehicleCondition) {
              this.selectionCriteria.push(item);
            }
          }
        },
        error: () => this.messageService.add({severity: 'error', summary: 'Error', detail: 'Unable to retrieve vehicle selection criteria'})
      });
  }
  /**
   * Updates the Cruise Control command status on page load
   */
  updateStatus(): void {
    this.cruiseControlService.getStatus(this.accountId, this.settings.vehicleCondition)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (status) => {
          this.status = status;
          this.updateProgress(status);
          this.lastCommandCreated = status.created;
          if (status.created && !status.completed) {
            this.startStatusPolling();
            this.messageService.add({severity: 'info', detail: `Cruise Control is currently running for ${this.label.toLowerCase()} inventory`});
          }
        },
        error: () => this.messageService.add({severity: 'error', summary: 'Error', detail: 'Unable to retrieve Cruise Control command status'})
      });
  }
  /**
   * Updates the progress index for the most recent Cruise Control command
   * @param status
   */
  updateProgress(status: CruiseControlStatusInterface): void {
    this.commandMessages = [];
    if (status.pending) {
      this.progressIndex = 0;
    }
    if (status.started) {
      this.progressIndex = 1;
    }
    if (status.completed) {
      this.progressIndex = 2;
      if (status.adsCreated === 0 || !status.adsCreated) {
        this.commandMessages.push({severity: 'success', detail: 'All offer sets are up to date.'});
      }
      else {
        this.commandMessages.push({severity: 'success', detail: `${status.adsCreated} offer sets created.`});
      }
    }
  }
  /**
   * Shows the modal to confirm the user wants to start a new cruise control command
   * @param event
   */
  confirmRun(event: MouseEvent): void {
    this.confirmationService.confirm({
      target: event.target,
      header: 'Cruise Control',
      message: this.label,
      icon: 'pi pi-car',
      acceptIcon: 'none',
      rejectIcon: 'none',
      accept: () => this.startCruiseControl()
    });
  }
  /**
   * Saves the changed settings values
   */
  updateSettings(): void {
    this.cruiseControlService.updateSettings(this.settings)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => this.messageService.add({
          severity: 'success', summary: 'Success', detail: 'Settings were updated'}),
        error: () => this.messageService.add({
          severity: 'error', summary: 'Error', detail: 'Failed to save settings'})
      });
  }
  /**
   * Starts a new Cruise Control command
   */
  startCruiseControl(): void {
    this.startStatusPolling();
    this.progressIndex = 0;
    this.commandMessages = [];
    this.messageService.add({
      severity: 'info', summary: 'Info', detail: `Starting Cruise Control for ${this.label.toLowerCase()} offer sets`});
    this.cruiseControlService.run(this.accountId, this.settings.vehicleCondition)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          // The command is queued, startStatusPolling will monitor it
        },
        error: () => {
          this.cancelStatusPolling();
          this.messageService.add({
            severity: 'error', summary: 'Error', detail: 'Cruise Control ran into an issue and could not complete. View status for more details.'});
        }
      });
  }
  /**
   * Polls the status of the most recent Cruise Control command
   */
  startStatusPolling(): void {
    this.cancelStatusPolling();
    this.cruiseControlService.getStatus(this.accountId, this.settings.vehicleCondition)
      .pipe(
        repeat({delay: 1000}),
        takeUntil(this.pollingSubject),
        takeUntil(this.destroySubject)
      )
      .subscribe({
        next: (status) => {
          if (this.lastCommandCreated !== status.created) {
            this.lastCommandCreated = status.created;
            this.monitorNewCommand = true;
          }
          if (this.monitorNewCommand) {
            this.status = status;
            this.updateProgress(status);
            if (status.completed) {
              this.monitorNewCommand = false;
              this.cancelStatusPolling();
              this.messageService.add({
                severity: 'success', summary: 'Success', detail: 'Cruise Control completed'});
            }
          }
        }
      });
  }
  /**
   * Stops polling the status of the most recent Cruise Control command
   */
  cancelStatusPolling(): void {
    this.pollingSubject.next(true);
  }

  /**
   * Checks if the Cruise Control command is in progress
   * @param status
   */
  isInProgress(status: CruiseControlStatusInterface | null): boolean {
    let result = false;

    if (status) {
      const isRunning = !status.canceled && ((status.started && !status.completed) || status.pending);
      result = isRunning || (status.manualProcess && !!status.created && (!status.completed || !status.canceled));
    }

    return result;
  }
}
